/**
 * @class
 * @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;

      this.button = this.querySelector('.load-more');
      this.grid = id ? document.querySelector('[data-id="' + id + '"]') : document.getElementById('product-grid');
      this.nextUrl = this.dataset.nextUrl || '';
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }

    connectedCallback() {
      if (!this.grid) {
        return;
      }

      if (this.classList.contains('pagination-type--loadmore')) {
        this.bindLoadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }

      this.toggleButtonVisibility();
    }

    bindLoadMore() {
      if (!this.button) {
        return;
      }

      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    infinite() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });

      this.observer.observe(this);
    }

    loadProducts() {
      const url = this.nextUrl;

      if (!url || this.hasAttribute('loading')) {
        return;
      }

      this.setAttribute('loading', true);

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          this.renderProducts(responseText, url);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', { url: url });
          }
        })
        .catch(() => {
          this.removeAttribute('loading');
        });
    }

    renderProducts(html, url) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const gridToAppend = parsed.getElementById('product-grid');

      if (!gridToAppend) {
        this.endPagination();
        return;
      }

      const products = Array.from(gridToAppend.querySelectorAll('.column'));

      if (!products.length) {
        this.endPagination();
        return;
      }

      products.forEach((product) => {
        this.grid.appendChild(product);

        if (typeof lazySizes !== 'undefined' && lazySizes.loader) {
          product.querySelectorAll('.lazyload').forEach((image) => {
            lazySizes.loader.unveil(image);
          });
        }
      });

      this.updateNextUrl(parsed);

      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: () => {
            this.removeAttribute('loading');
          }
        });
      } else {
        this.removeAttribute('loading');
      }

      this.toggleButtonVisibility();
    }

    updateNextUrl(parsedDocument) {
      const sectionId = this.dataset.section;
      let nextPagination = null;

      if (sectionId) {
        nextPagination = parsedDocument.querySelector('pagination-theme[data-section="' + sectionId + '"]');
      }

      if (!nextPagination) {
        nextPagination = parsedDocument.querySelector('pagination-theme');
      }

      this.nextUrl = nextPagination ? (nextPagination.dataset.nextUrl || '') : '';
      this.dataset.nextUrl = this.nextUrl;

      if (!this.nextUrl && this.observer) {
        this.observer.unobserve(this);
      }
    }

    toggleButtonVisibility() {
      if (!this.button) {
        return;
      }

      this.button.classList.toggle('visually-hidden', !this.nextUrl);
    }

    endPagination() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.removeAttribute('loading');
      this.toggleButtonVisibility();

      if (this.observer) {
        this.observer.unobserve(this);
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}